<script setup lang="ts">
import type { AccordionTriggerProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { ChevronDown, Minus, Plus } from 'lucide-vue-next'
import {
  AccordionHeader,
  AccordionTrigger,

} from 'reka-ui'
import { cn } from '@/lib/utils'

interface Props extends AccordionTriggerProps {
  class?: HTMLAttributes['class']
  iconType?: 'chevron' | 'plusMinus'
  iconSize?: 'sm' | 'md' | 'lg' | 'xl'
}

const props = withDefaults(defineProps<Props>(), {
  iconType: 'chevron',
  iconSize: 'md',
})

const delegatedProps = reactiveOmit(props, 'class', 'iconType', 'iconSize')

const iconSizeClass = computed(() => {
  const sizeMap = {
    sm: 'size-3',
    md: 'size-4',
    lg: 'size-5',
    xl: 'size-6',
  }
  return sizeMap[props.iconSize]
})
</script>

<template>
  <AccordionHeader class="flex">
    <AccordionTrigger
      data-slot="accordion-trigger"
      v-bind="delegatedProps"
      :class="
        cn(
          'focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50',
          iconType === 'chevron' && '[&[data-state=open]>svg]:rotate-180',
          props.class,
        )
      "
    >
      <slot />
      <slot name="icon">
        <template v-if="iconType === 'chevron'">
          <ChevronDown
            :class="cn('text-muted-foreground pointer-events-none shrink-0 translate-y-0.5 transition-transform duration-200', iconSizeClass)"
          />
        </template>
        <template v-else-if="iconType === 'plusMinus'">
          <span :class="cn('relative flex items-center justify-center shrink-0 translate-y-0.5', iconSizeClass)">
            <Plus
              :class="cn('text-muted-foreground pointer-events-none absolute transition-all duration-200 [button[data-state=open]_&]:opacity-0 [button[data-state=open]_&]:rotate-90', iconSizeClass)"
            />
            <Minus
              :class="cn('text-muted-foreground pointer-events-none absolute transition-all duration-200 [button[data-state=closed]_&]:opacity-0 [button[data-state=closed]_&]:rotate-90', iconSizeClass)"
            />
          </span>
        </template>
      </slot>
    </AccordionTrigger>
  </AccordionHeader>
</template>
